import type { ModuleDef } from './types.js';

/** Operations: sites, camps, organisation units, suppliers, announcements and devices. */
export const OPERATIONS_MODULES: readonly ModuleDef[] = [
  {
    key: 'worksites',
    label: 'Sites',
    group: 'operations',
    permissions: [
      { action: 'view', defaultRoles: ['HR', 'MANAGER'], description: 'Open site lists, boards and attendance history' },
      { action: 'create', defaultRoles: ['HR'], description: 'Add a site' },
      { action: 'edit', defaultRoles: ['HR'], description: 'Change a site record' },
      { action: 'delete', defaultRoles: [], description: 'Remove a site' },
      { action: 'manage', defaultRoles: [], description: 'Bulk-import sites' },
    ],
  },
  {
    key: 'worksites.assignments',
    label: 'Site assignments',
    group: 'operations',
    permissions: [
      { action: 'view', defaultRoles: ['HR'], description: 'See who is assigned to a site' },
      { action: 'create', defaultRoles: ['HR'], description: 'Assign people to a site' },
      { action: 'delete', defaultRoles: ['HR'], description: 'Remove people from a site' },
    ],
  },
  {
    key: 'worksites.time',
    label: 'Site time',
    group: 'operations',
    permissions: [
      { action: 'view', defaultRoles: ['HR'], description: 'See live and daily time worked on a site' },
    ],
  },
  {
    key: 'camps.occupancy',
    label: 'Camp occupancy',
    group: 'operations',
    permissions: [
      { action: 'view', defaultRoles: ['HR', 'MANAGER'], description: 'See camp occupancy against capacity' },
    ],
  },
  {
    key: 'departments',
    label: 'Departments',
    group: 'operations',
    permissions: [
      { action: 'view', defaultRoles: ['HR'], description: 'Open a single department record' },
      { action: 'create', defaultRoles: ['HR'], description: 'Add a department' },
      { action: 'edit', defaultRoles: ['HR'], description: 'Change a department' },
      { action: 'delete', defaultRoles: [], description: 'Remove a department' },
    ],
  },
  {
    key: 'designations',
    label: 'Designations',
    group: 'operations',
    permissions: [
      { action: 'create', defaultRoles: ['HR'], description: 'Add a designation' },
      { action: 'edit', defaultRoles: ['HR'], description: 'Change a designation' },
      { action: 'delete', defaultRoles: [], description: 'Remove a designation' },
    ],
  },
  {
    key: 'legalEntities',
    label: 'Legal entities',
    group: 'operations',
    permissions: [
      { action: 'view', defaultRoles: ['HR', 'MANAGER'], description: 'See legal entities' },
      { action: 'create', defaultRoles: ['HR'], description: 'Add a legal entity' },
      { action: 'edit', defaultRoles: ['HR'], description: 'Change a legal entity' },
      { action: 'delete', defaultRoles: [], description: 'Remove a legal entity' },
    ],
  },
  {
    key: 'laborSuppliers',
    label: 'Labour suppliers',
    group: 'operations',
    permissions: [
      { action: 'view', defaultRoles: ['HR', 'MANAGER'], description: 'See labour suppliers' },
      { action: 'create', defaultRoles: ['HR'], description: 'Add a labour supplier' },
      { action: 'edit', defaultRoles: ['HR'], description: 'Change a labour supplier' },
      { action: 'delete', defaultRoles: [], description: 'Remove a labour supplier' },
    ],
  },
  {
    key: 'announcements',
    label: 'Announcements',
    group: 'operations',
    permissions: [
      { action: 'view', defaultRoles: ['HR'], description: 'See the announcement management list' },
      { action: 'create', defaultRoles: ['HR'], description: 'Publish an announcement' },
      { action: 'edit', defaultRoles: ['HR'], description: 'Change an announcement' },
      { action: 'delete', defaultRoles: ['HR'], description: 'Remove an announcement' },
    ],
  },
  {
    key: 'devices',
    label: 'Devices',
    group: 'operations',
    permissions: [
      { action: 'view', defaultRoles: ['HR'], description: 'See devices, terminals and their activity' },
      { action: 'create', defaultRoles: [], platform: true, description: 'Register a terminal' },
      { action: 'edit', defaultRoles: [], platform: true, description: 'Change a device fixed location' },
      { action: 'delete', defaultRoles: [], platform: true, description: 'Remove devices and clear registrations' },
      { action: 'manage', defaultRoles: [], platform: true, description: 'Sync terminals and replay quarantined punches' },
    ],
  },
  {
    key: 'devices.approvals',
    label: 'Device approvals',
    group: 'operations',
    permissions: [
      { action: 'view', defaultRoles: ['HR'], description: 'See devices waiting for approval' },
      { action: 'approve', defaultRoles: [], platform: true, description: 'Approve, reject or revoke a device' },
    ],
  },
  {
    key: 'field.enrolment',
    label: 'Field face enrolment',
    group: 'operations',
    permissions: [
      { action: 'create', defaultRoles: ['HR'], description: 'Enrol a worker face from the field app' },
    ],
  },
  {
    key: 'field.events',
    label: 'Field punch details',
    group: 'operations',
    permissions: [
      { action: 'view', defaultRoles: ['HR', 'MANAGER'], description: 'Open the detail of a field punch' },
    ],
  },
  {
    key: 'field.registration',
    label: 'Field registration codes',
    group: 'operations',
    permissions: [
      { action: 'manage', defaultRoles: [], platform: true, description: 'Issue codes that register field devices' },
    ],
  },
  {
    key: 'field.operators',
    label: 'Field operators',
    group: 'operations',
    permissions: [
      { action: 'create', defaultRoles: ['HR'], description: 'Create site operators for the field app' },
    ],
  },
  {
    key: 'field.telemetry',
    label: 'Field telemetry',
    group: 'operations',
    permissions: [
      { action: 'view', defaultRoles: ['HR', 'MANAGER'], description: 'See field device health summaries' },
    ],
  },
  {
    key: 'field.templateUpgrades',
    label: 'Face template upgrades',
    group: 'operations',
    permissions: [
      { action: 'view', defaultRoles: ['HR'], description: 'See the status of face template upgrades' },
    ],
  },
];
